import { useCallback, useEffect, useRef, useState } from 'react';
import { apiRequest } from '../utils/auth';

export interface CursorPage<T> {
  success: boolean;
  data?: T[];
  items?: T[];
  pagination?: { hasMore: boolean; nextCursor: string | null; total?: number };
}

/** Keep loaded rows during paging/retry and ignore responses from a closed or changed feed. */
export function useCursorFeed<T, R extends CursorPage<T> = CursorPage<T>>(endpoint: string | null, idKey: keyof T, prefixKey?: keyof T) {
  const generation = useRef(0);
  const busy = useRef(false);
  const cursor = useRef<string | null>(null);
  const [state, setState] = useState({ endpoint, items: [] as T[], response: null as R | null,
    loading: !!endpoint, error: null as string | null, hasMore: false });

  const load = useCallback(async (before: string | null, replace: boolean, owner: number) => {
    if (!endpoint || busy.current) return;
    busy.current = true;
    setState(previous => ({ ...previous, loading: true, error: null }));
    try {
      const response = await apiRequest<R>(`${endpoint}${before ? `&before=${encodeURIComponent(before)}` : ''}`);
      if (!response.success) throw new Error('Please try again.');
      if (owner !== generation.current) return;
      const page = response.items ?? response.data ?? [];
      const nextCursor = response.pagination?.nextCursor ?? null;
      cursor.current = nextCursor;
      setState(previous => {
        const rows = replace ? page : [...previous.items, ...page];
        const seen = new Set<unknown>();
        return { endpoint, items: rows.filter(item => {
          const key = prefixKey ? `${String(item[prefixKey])}:${String(item[idKey])}` : item[idKey];
          if (seen.has(key)) return false;
          seen.add(key); return true;
        }), response: replace ? response : previous.response, loading: false, error: null,
        hasMore: !!response.pagination?.hasMore && !!nextCursor && nextCursor !== before };
      });
    } catch (error) {
      if (owner === generation.current) setState(previous => ({ ...previous, loading: false,
        error: error instanceof Error ? error.message : 'Please try again.' }));
    } finally {
      if (owner === generation.current) busy.current = false;
    }
  }, [endpoint, idKey, prefixKey]);

  useEffect(() => {
    const owner = ++generation.current;
    busy.current = false; cursor.current = null;
    setState({ endpoint, items: [], response: null, loading: !!endpoint, error: null, hasMore: false });
    if (endpoint) void load(null, true, owner);
    return () => { generation.current += 1; };
  }, [endpoint, load]);

  const loadMore = useCallback(() => {
    if (cursor.current) void load(cursor.current, false, generation.current);
  }, [load]);
  const retry = useCallback(() => { void load(cursor.current, !cursor.current, generation.current); }, [load]);
  const current = state.endpoint === endpoint ? state : { items: [] as T[], response: null, loading: !!endpoint, error: null, hasMore: false };
  return { ...current, loadMore, retry };
}
